// console.log('variant-info-handler.js loading...');

/**
 * Handles dynamic variant-specific information display
 * Updates content based on selected variant's metafields or hardcoded content
 */
class VariantInfoHandler {
  constructor() {
    this.currentVariantId = null;
    this.variantInfoContainer = null;
    this.variantData = new Map();
    this.initTimeout = null;

    // Store bound handlers for cleanup
    this.boundHandlers = {
      handleVariantChange: this.handleVariantChange.bind(this),
      handleVariantUpdate: this.handleVariantUpdate.bind(this),
      setupListeners: this.setupListeners.bind(this)
    };

    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.boundHandlers.setupListeners);
    } else {
      this.setupListeners();
    }
  }

  setupListeners() {
    // console.log('Setting up variant info listeners');

    // Find the container for variant info
    this.variantInfoContainer = document.querySelector('.additional-variant-info');

    if (!this.variantInfoContainer) {
      // console.log('No additional-variant-info container found, creating one');
      // Try to create it after the variant picker if it doesn't exist
      this.createInfoContainer();
    }

    // Load variant data from page
    this.loadVariantData();

    // Listen for variant selection events
    document.addEventListener('variant:selected', this.boundHandlers.handleVariantChange);
    document.addEventListener('variant:update', this.boundHandlers.handleVariantUpdate);

    // Check initial variant on page load
    this.initTimeout = setTimeout(() => {
      this.checkInitialVariant();
    }, 500);
  }

  destroy() {
    // Remove all event listeners to prevent memory leaks
    document.removeEventListener('DOMContentLoaded', this.boundHandlers.setupListeners);
    document.removeEventListener('variant:selected', this.boundHandlers.handleVariantChange);
    document.removeEventListener('variant:update', this.boundHandlers.handleVariantUpdate);

    // Clear timeout if it exists
    if (this.initTimeout) {
      clearTimeout(this.initTimeout);
      this.initTimeout = null;
    }
  }

  createInfoContainer() {
    // Try to find a good place to insert the container
    const variantPicker = document.querySelector('variant-picker, .variant-picker, [data-variant-picker]');
    const productForm = document.querySelector('product-form-component, .product-form, form[data-type="add-to-cart-form"]');

    const container = document.createElement('div');
    container.className = 'additional-variant-info';
    container.style.cssText = 'margin: 20px 0; transition: all 0.3s ease;';

    if (variantPicker && variantPicker.parentNode) {
      // Insert after variant picker
      variantPicker.parentNode.insertBefore(container, variantPicker.nextSibling);
      this.variantInfoContainer = container;
      // console.log('Created variant info container after variant picker');
    } else if (productForm && productForm.parentNode) {
      // Insert before product form
      productForm.parentNode.insertBefore(container, productForm);
      this.variantInfoContainer = container;
      // console.log('Created variant info container before product form');
    }
  }

  loadVariantData() {
    // Try to load variant data from script tag or data attributes
    const variantDataScript = document.querySelector('#variant-metafields-data');

    if (variantDataScript) {
      try {
        const data = JSON.parse(variantDataScript.textContent);
        Object.entries(data).forEach(([variantId, info]) => {
          // Use the data as-is since it's now plain HTML from multi-line text fields
          this.variantData.set(variantId, info);
        });
        // console.log('Loaded variant data from script tag:', this.variantData);
      } catch (e) {
        // console.error('Error parsing variant data:', e);
      }
    }

    // Fallback: Hardcoded content for specific variants (can be customized)
    // This is where you can add specific content for your variants
    this.addHardcodedVariantData();
  }

  addHardcodedVariantData() {
    // Example: Add hardcoded content for specific variant IDs
    // Replace these IDs with your actual variant IDs

    // Monthly subscription variant (from your screenshot)
    if (!this.variantData.has('47161193365729')) {
      this.variantData.set('47161193365729', {
        subscriptionInfo: `
          <div class="variant-subscription-info" style="background: #FFF9E6; border: 1px solid #FFD700; border-radius: 8px; padding: 16px; margin: 16px 0;">
            <h4 style="margin: 0 0 12px 0; color: #333; font-weight: 600;">
              📦 Monthly Subscription Details
            </h4>
            <ul style="margin: 8px 0; padding-left: 20px; color: #555;">
              <li>Billed monthly at $19.99</li>
              <li>Cancel anytime with no fees</li>
              <li>Free shipping on all orders</li>
              <li>Priority customer support</li>
              <li>Access to exclusive features</li>
            </ul>
            <p style="margin: 12px 0 0 0; font-size: 14px; color: #666;">
              <strong>Note:</strong> This subscription automatically renews each month until canceled.
            </p>
          </div>
        `,
        additionalDetails: ''
      });
    }

    // Annual subscription variant
    if (!this.variantData.has('47161193398497')) {
      this.variantData.set('47161193398497', {
        subscriptionInfo: `
          <div class="variant-subscription-info" style="background: #FFF9E6; border: 1px solid #FFD700; border-radius: 8px; padding: 16px; margin: 16px 0;">
            <h4 style="margin: 0 0 12px 0; color: #333; font-weight: 600;">
              📦 Annual Subscription Details
            </h4>
            <ul style="margin: 8px 0; padding-left: 20px; color: #555;">
              <li>Billed annually at $199.99</li>
              <li>Save over $39 compared to monthly!</li>
              <li>Free shipping on all orders</li>
              <li>Priority customer support</li>
              <li>Access to exclusive features</li>
              <li>Annual subscriber bonus rewards</li>
            </ul>
            <p style="margin: 12px 0 0 0; font-size: 14px; color: #666;">
              <strong>Note:</strong> This subscription automatically renews each year until canceled.
            </p>
          </div>
        `,
        additionalDetails: ''
      });
    }

    // HSA/FSA eligible variant
    if (!this.variantData.has('47239926350049')) {
      this.variantData.set('47239926350049', {
        subscriptionInfo: `
          <div class="variant-subscription-info" style="background: #E8F5E9; border: 1px solid #4CAF50; border-radius: 8px; padding: 16px; margin: 16px 0;">
            <h4 style="margin: 0 0 12px 0; color: #2E7D32; font-weight: 600;">
              ✓ HSA/FSA Eligible Product
            </h4>
            <p style="margin: 8px 0; color: #555;">
              This is a one-time purchase eligible for HSA/FSA payment.
            </p>
            <ul style="margin: 8px 0; padding-left: 20px; color: #555;">
              <li>Use your HSA/FSA card at checkout</li>
              <li>Pre-tax savings on qualified medical expenses</li>
              <li>Receipt provided for reimbursement</li>
              <li>No subscription required</li>
            </ul>
            <p style="margin: 12px 0 0 0; font-size: 14px; color: #666;">
              <strong>Important:</strong> This is not a recurring subscription. One-time purchase only.
            </p>
          </div>
        `,
        additionalDetails: ''
      });
    }

    // console.log('Added hardcoded variant data');
  }

  checkInitialVariant() {
    // Check the currently selected variant on page load
    const variantIdInput = document.querySelector('input[name="id"][type="hidden"], select[name="id"]');

    if (variantIdInput && variantIdInput.value) {
      // console.log('Initial variant found:', variantIdInput.value);
      this.updateVariantInfo(variantIdInput.value);
    }
  }

  handleVariantChange(event) {
    if (!event.detail || !event.detail.resource) return;

    const variantId = String(event.detail.resource.id);
    // console.log('Variant selected:', variantId);

    this.updateVariantInfo(variantId);
  }

  handleVariantUpdate(event) {
    if (!event.detail || !event.detail.resource) return;

    const variantId = String(event.detail.resource.id);
    // console.log('Variant updated:', variantId);

    this.updateVariantInfo(variantId);
  }

  updateVariantInfo(variantId) {
    if (!this.variantInfoContainer) {
      // console.log('No variant info container available');
      return;
    }

    // Skip if same variant
    if (this.currentVariantId === variantId) {
      return;
    }

    this.currentVariantId = variantId;

    // Get variant-specific content
    const variantInfo = this.variantData.get(variantId);

    if (variantInfo && (variantInfo.subscriptionInfo || variantInfo.additionalDetails)) {
      // console.log('Updating variant info for:', variantId);

      // Fade out current content
      this.variantInfoContainer.style.opacity = '0';

      setTimeout(() => {
        // Update content
        let htmlContent = '';

        if (variantInfo.subscriptionInfo) {
          // Check if the content is already wrapped in HTML tags
          const content = variantInfo.subscriptionInfo;
          if (content && content !== 'null' && content !== null) {
            // If it's plain text (no HTML tags), wrap it
            if (!content.includes('<') && !content.includes('>')) {
              htmlContent += `<div class="variant-subscription-info">${content}</div>`;
            } else {
              // Already has HTML, use as is
              htmlContent += content;
            }
          }
        }

        if (variantInfo.additionalDetails) {
          const content = variantInfo.additionalDetails;
          if (content && content !== 'null' && content !== null) {
            if (!content.includes('<') && !content.includes('>')) {
              htmlContent += `<div class="variant-additional-details">${content}</div>`;
            } else {
              htmlContent += content;
            }
          }
        }

        // If we still have content, wrap it in the styled container
        if (htmlContent) {
          // Add wrapper if not already present
          if (!htmlContent.includes('variant-subscription-info')) {
            htmlContent = `<div class="variant-subscription-info">${htmlContent}</div>`;
          }
        }

        this.variantInfoContainer.innerHTML = htmlContent;

        // Fade in new content
        this.variantInfoContainer.style.opacity = '1';
      }, 150);
    } else {
      // console.log('No variant info found for:', variantId);

      // Clear the container if no info for this variant
      this.variantInfoContainer.style.opacity = '0';
      setTimeout(() => {
        this.variantInfoContainer.innerHTML = '';
      }, 150);
    }
  }

  // Method to add/update variant data dynamically
  setVariantInfo(variantId, subscriptionInfo, additionalDetails = '') {
    this.variantData.set(String(variantId), {
      subscriptionInfo,
      additionalDetails
    });

    // Update display if this variant is currently selected
    if (this.currentVariantId === String(variantId)) {
      this.updateVariantInfo(variantId);
    }
  }
}

// Initialize the handler
if (!window.variantInfoHandler) {
  // console.log('Initializing VariantInfoHandler');
  window.variantInfoHandler = new VariantInfoHandler();
  // console.log('VariantInfoHandler initialized');
} else {
  // console.log('VariantInfoHandler already exists');
}